import React, { useState, Component, ErrorInfo, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { TourOperatorModal, ExtendedFormData } from '../components/TourOperatorModal';
import { sanitizeInput, isValidEmail, isValidYear, isValidFullName, isValidLeadStatus } from '../lib/validation';
import { DEFAULT_API_ENDPOINT, getWidgetConfig } from '../lib/widget-config';

interface WidgetConfig {
  apiEndpoint: string;
  buttonText?: string;
  buttonClassName?: string;
  additionalButtonClasses?: string;  // New: adds to default class
  appSource?: string;
  theme?: {
    primaryColor?: string;      // Button background color
    textColor?: string;          // Button text color (optional, defaults to white)
    backgroundColor?: string;    // Modal background color (optional)
    fontSize?: string;           // Button font size (optional, e.g., '20px', '1.2rem')
  };
  buttonStyles?: React.CSSProperties;  // Any CSS properties for the button
}

interface ToastState {
  show: boolean;
  message: string;
  type: 'success' | 'error';
}

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Safari Lead Form Widget Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c00',
          fontFamily: 'system-ui, sans-serif'
        }}>
          <h3 style={{ margin: '0 0 10px' }}>Widget Error</h3>
          <p style={{ margin: 0 }}>The Safari Lead Form widget encountered an error. Please refresh the page and try again.</p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre style={{ marginTop: '10px', fontSize: '12px' }}>{this.state.error.toString()}</pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

function SafariLeadFormWidget({ config }: { config: WidgetConfig }) {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldResetForm, setShouldResetForm] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    show: false,
    message: '',
    type: 'success'
  });

  const handleSubmit = async (data: ExtendedFormData) => {
    setIsSubmitting(true);
    setShouldResetForm(false);

    // Additional validation layer (belt and suspenders approach)
    if (!isValidFullName(data.fullName) ||
        !isValidEmail(data.email) ||
        !isValidYear(data.yearOfTravel) ||
        !isValidLeadStatus(data.status)) {
      console.error('Invalid data detected before submission');
      setToast({
        show: true,
        message: 'Please check your input and try again.',
        type: 'error'
      });
      setIsSubmitting(false);
      setTimeout(() => {
        setToast({ show: false, message: '', type: 'error' });
      }, 5000);
      return;
    }

    try {
      // Prepare extended data for submission
      const sanitizedData = {
        appSource: config.appSource || 'WORDPRESS',
        fullName: sanitizeInput(data.fullName),
        email: sanitizeInput(data.email),
        yearOfTravel: sanitizeInput(data.yearOfTravel),
        status: data.status, // Already validated
        // New extended fields
        destination: data.destination || '',
        travelCompanions: data.travelCompanions || '',
        budget: data.budget || '',
        safariNights: data.safariNights || '',
        // Legacy fields
        journeyData: null,
        recommendations: null,
      };

      const response = await fetch(config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setIsSubmitting(false);
      setShouldResetForm(true);

      // Show success toast and close modal after delay
      setToast({
        show: true,
        message: 'Thank you! A tour operator will contact you soon.',
        type: 'success'
      });

      setTimeout(() => {
        setShowModal(false);
        setShouldResetForm(false);
      }, 2000);

      setTimeout(() => {
        setToast({ show: false, message: '', type: 'success' });
      }, 5000);
    } catch (error) {
      console.error('Error submitting to Airtable:', error);
      setIsSubmitting(false);

      // Show error toast but don't close modal so user can try again
      setToast({
        show: true,
        message: 'There was an error submitting your request. Please try again.',
        type: 'error'
      });

      setTimeout(() => {
        setToast({ show: false, message: '', type: 'error' });
      }, 5000);
    }
  };

  const buttonStyle: React.CSSProperties = {};
  if (config.theme?.primaryColor) {
    buttonStyle.backgroundColor = config.theme.primaryColor;
  }
  if (config.theme?.textColor) {
    buttonStyle.color = config.theme.textColor;
  }
  if (config.theme?.fontSize) {
    buttonStyle.fontSize = config.theme.fontSize;
  }
  // Apply any custom button styles (these override theme if there's overlap)
  if (config.buttonStyles) {
    Object.assign(buttonStyle, config.buttonStyles);
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setShowModal(true)}
        className={
          config.buttonClassName ||
          `safari-lead-form-button ${config.additionalButtonClasses || ''}`.trim()
        }
        style={buttonStyle}
      >
        {config.buttonText || 'Match Me With a Tour Operator'}
      </button>

      {/* Modal */}
      <TourOperatorModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        isLoading={isSubmitting}
        shouldReset={shouldResetForm}
        extendedMode={true}
      />

      {/* Toast Notification */}
      {toast.show && (
        <div className="safari-toast-container">
          <div className={`safari-toast safari-toast-${toast.type}`}>
            <div className={`safari-toast-icon safari-toast-icon-${toast.type}`}>
              {toast.type === 'success' ? (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="safari-toast-message">{toast.message}</p>
          </div>
        </div>
      )}
    </>
  );
}

// Global initialization function
declare global {
  interface Window {
    SafariLeadForm: {
      init: (config: WidgetConfig, targetElementId?: string) => void;
      instances: Map<string, any>;
    };
  }
}

// Track widget instances to prevent conflicts
let instanceCounter = 0;
const widgetInstances = new Map<string, any>();

window.SafariLeadForm = {
  instances: widgetInstances,
  init: (config: WidgetConfig, targetElementId?: string) => {
    // Use default endpoint if not provided
    const finalConfig = {
      ...config,
      apiEndpoint: config?.apiEndpoint || DEFAULT_API_ENDPOINT
    };

    // Validate config
    if (!finalConfig.apiEndpoint) {
      console.error('Safari Lead Form Widget: Unable to determine API endpoint');
      return;
    }

    // Generate unique instance ID if not provided
    const instanceId = targetElementId || `safari-lead-form-root-${++instanceCounter}`;
    // Inject default styles if not already present
    if (!document.getElementById('safari-lead-form-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'safari-lead-form-styles';
      styleElement.textContent = `
        /* Keyframe Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        /* Default Button Styles - Matches website style */
        .safari-lead-form-button {
          background-color: #E6A87E;
          color: #ffffff;
          padding: 18px 40px;
          border: none;
          border-radius: 8px;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          display: inline-block;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          min-width: 250px;
        }

        .safari-lead-form-button:hover {
          background-color: #D4976D;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
        }

        .safari-lead-form-button:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* Utility classes */
        .no-shadow {
          box-shadow: none !important;
        }
        .no-shadow:hover {
          box-shadow: none !important;
        }
        .no-shadow:active {
          box-shadow: none !important;
        }

        /* Font size utilities */
        .text-sm { font-size: 14px !important; }
        .text-base { font-size: 16px !important; }
        .text-lg { font-size: 18px !important; }
        .text-xl { font-size: 20px !important; }
        .text-2xl { font-size: 24px !important; }
        .text-3xl { font-size: 30px !important; }

        /* Mobile Background Color */
        .bg-mobile-background {
          background-color: #F6F7E9;
        }

        /* Toast Notification Styles */
        .safari-toast-container {
          position: fixed;
          top: 24px;
          left: 0;
          right: 0;
          z-index: 9999;
          display: flex;
          justify-content: center;
          padding: 0 16px;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .safari-toast {
          padding: 16px 24px;
          border-radius: 16px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          gap: 16px;
          max-width: 448px;
          width: 100%;
        }

        .safari-toast-success {
          background-color: rgba(255, 255, 255, 0.95);
          border: 2px solid rgba(76, 175, 80, 0.2);
        }

        .safari-toast-error {
          background-color: rgba(255, 255, 255, 0.95);
          border: 2px solid rgba(239, 68, 68, 0.2);
        }

        .safari-toast-icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .safari-toast-icon-success {
          background-color: #4CAF50;
        }

        .safari-toast-icon-error {
          background-color: #EF4444;
        }

        .safari-toast-message {
          font-weight: 500;
          font-size: 16px;
          color: #2C4A52 !important;
          margin: 0;
        }
      `;
      document.head.appendChild(styleElement);
    }

    // Find or create root element
    let rootElement = document.getElementById(instanceId);
    if (!rootElement) {
      // If a specific target wasn't provided, look for the default container
      rootElement = document.getElementById('safari-lead-form-root');

      if (!rootElement) {
        // Create a new container
        rootElement = document.createElement('div');
        rootElement.id = instanceId;
        document.body.appendChild(rootElement);
      } else {
        // Use existing element but ensure unique ID for multiple instances
        rootElement.id = instanceId;
      }
    }

    try {
      // Clean up existing instance if present
      if (widgetInstances.has(instanceId)) {
        const existingRoot = widgetInstances.get(instanceId);
        if (existingRoot && existingRoot.unmount) {
          existingRoot.unmount();
        }
      }

      // Render the widget with error boundary
      const root = createRoot(rootElement);
      root.render(
        <ErrorBoundary>
          <SafariLeadFormWidget config={finalConfig} />
        </ErrorBoundary>
      );

      // Store instance reference
      widgetInstances.set(instanceId, root);
    } catch (error) {
      console.error('Safari Lead Form Widget initialization error:', error);

      // Fallback error display
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="padding: 20px; background: #fee; border: 1px solid #fcc; border-radius: 8px; color: #c00;">
            <strong>Widget Error:</strong> Failed to initialize Safari Lead Form. Please check the console for details.
          </div>
        `;
      }
    }
  }
};

// Auto-initialize if config is provided via data attributes
document.addEventListener('DOMContentLoaded', () => {
  const scriptTag = document.querySelector('script[src*="safari-lead-form"]');
  if (scriptTag) {
    const apiEndpoint = scriptTag.getAttribute('data-api-endpoint');
    const buttonText = scriptTag.getAttribute('data-button-text');

    if (apiEndpoint) {
      window.SafariLeadForm.init({
        apiEndpoint,
        buttonText: buttonText || undefined,
      });
    }
  }
});
